import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import toQueryParams from 'jquery-param'

import { observer } from 'mobx-react'

import PanesHeader from 'modules/core/components/PanesHeader'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import FileViewer from 'modules/documents/components/FileViewer'
import type { DocumentTemplate } from 'modules/documents/hooks/useGetDocumentTemplates'
import { usePreviewVariablesDocumentTemplate } from 'modules/documents/hooks/usePreviewVariablesDocumentTemplate'
import variables from 'modules/documents/store/collections/variables'
import type Variable from 'modules/documents/store/models/Variable'
import employees from 'modules/employees/store/collections/employees'

import EmployeePreviewSelector from './EmployeePreviewSelector'
import VariablesList from './VariablesList'

interface Props {
  documentTemplate: DocumentTemplate
}

const DocumentViewer = ({ documentTemplate }: Props) => {
  const [url, setUrl] = React.useState<string>()
  const [employeeId, setEmployeeId] = React.useState<number | null>()
  const [loading, setLoading] = React.useState<boolean>(true)
  const [templateVariables, setTemplateVariables] = React.useState<Variable[]>([])
  const { variableValues, loading: loadingPreview } = usePreviewVariablesDocumentTemplate({
    id: documentTemplate.id!,
    employeeId,
  })
  const values = useMemo(() => documentTemplate.values || [], [documentTemplate.id])
  const filename = documentTemplate.filename
  const isDocx = documentTemplate.extension === '.docx'
  const hasVariables = !!documentTemplate.values?.length

  useEffect(() => {
    const query = toQueryParams({
      employee_id: employeeId,
      id: documentTemplate.id,
      filename,
    })

    setUrl(buildUrl(`/templates/preview?${query}`))

    setLoading(true)
  }, [employeeId, filename, documentTemplate.id])

  useUnsafeEffect(() => {
    variables.allVariables().then(() => {
      const filteredTemplateVariables = variables.filter((variable) =>
        values.includes(variable.attributes.variable)
      )
      setTemplateVariables(filteredTemplateVariables)
    })
  }, [values])

  const onEmployeeSelected = (accessIds: number[]) => {
    const accessId = accessIds[0] == undefined ? null : accessIds[0]

    const employeeId = employees.find({
      access_id: accessId,
    })?.id

    setEmployeeId(employeeId)
  }

  const EmployeeSelectorHeader = (
    <PanesHeader
      illustration="template"
      title={i18n.t('documents.document_templates.preview.title')}
      size="500"
    />
  )

  const renderTemplatePreview = () => {
    if (!url || !filename) return null

    const exportFilename = isDocx ? filename.replace('.docx', '.pdf') : filename

    return (
      <FileViewer
        url={url}
        filename={exportFilename}
        mimeType="application/pdf"
        isUrlAbsolute
        onPdfLoaded={() => setLoading(false)}
      />
    )
  }

  return (
    <Box paddingX="s48" paddingBottom="s48" gap="s8">
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Box>{EmployeeSelectorHeader}</Box>
        <Box
          style={{ minWidth: '447px', width: '447px' }}
          background="grey200"
          padding="s24"
          borderRadius={{
            all: 'abs008',
          }}
        >
          <EmployeePreviewSelector
            onEmployeeSelected={onEmployeeSelected}
            readonly={!hasVariables}
          />
        </Box>
      </Box>

      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        borderRadius={{
          all: 'abs006',
        }}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Box width="none" flexGrow>
          {renderTemplatePreview()}
        </Box>
        <Box style={{ minWidth: '447px', width: '447px' }} padding="s24">
          <VariablesList
            documentTemplate={documentTemplate}
            templateVariables={templateVariables}
            variableValues={variableValues}
            loading={loading}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(DocumentViewer)
